<template>
  <div class="family-auth family-page w-full bg-gradient-to-b from-[#F0F7FC] via-white to-slate-50">
    <header
      class="family-page-header px-4 py-3 flex items-center"
      :style="{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }"
    >
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center text-slate-500 shadow-sm"
        aria-label="返回"
        @click="$router.push('/portal')"
      >
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </header>

    <main class="family-page-body px-5 pb-8">
      <div class="flex items-center gap-3 mb-8">
        <img :src="brandLogo" alt="安心陪护" class="family-brand-logo" />
        <div>
          <h1 class="text-xl font-bold text-slate-900">安心陪护</h1>
          <p class="text-xs text-slate-400 mt-0.5">家属端 · 登录</p>
        </div>
      </div>

      <div class="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100/60">
        <h2 class="text-lg font-bold text-slate-800 mb-1">欢迎回来</h2>
        <p class="text-sm text-slate-400 mb-6">使用手机号登录，管理陪护订单</p>

        <form @submit.prevent="handleLogin">
          <label class="block mb-4">
            <span class="text-sm text-slate-600">手机号</span>
            <input
              v-model.trim="form.mobile"
              type="tel"
              maxlength="11"
              placeholder="请输入手机号"
              class="auth-field"
            />
          </label>

          <label class="block mb-4">
            <span class="text-sm text-slate-600">密码</span>
            <div class="relative mt-1.5">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="请输入密码"
                class="auth-field pr-10"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
          </label>

          <div class="flex items-center justify-between mb-6">
            <label class="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
              <input v-model="remember" type="checkbox" class="rounded border-slate-300 text-family-blue focus:ring-family-blue" />
              记住我
            </label>
            <button type="button" class="text-sm text-family-blue" @click="forgotHint">忘记密码？</button>
          </div>

          <button
            type="button"
            class="w-full py-3.5 rounded-xl text-white text-sm font-semibold bg-family-blue shadow-[0_6px_20px_rgb(0,130,212,0.35)] transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
            :disabled="loading"
            @click="handleLogin"
          >
            <svg v-if="loading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ loading ? "登录中..." : "登录" }}
          </button>
        </form>

        <p class="text-center text-sm text-slate-500 mt-6">
          还没有账号？
          <button type="button" class="text-family-blue font-medium" @click="$router.push('/family/register')">
            立即注册
          </button>
        </p>
      </div>

      <p class="text-center text-xs text-slate-300 mt-8">登录即表示同意《服务协议》和《隐私政策》</p>
    </main>
  </div>
</template>

<script>
import { familyAuth } from "./familyAuth";
import brandLogo from "../../common/brandLogo";

export default {
  name: "FamilyLogin",
  data() {
    return {
      brandLogo,
      form: { mobile: "", password: "" },
      showPassword: false,
      remember: true,
      loading: false,
    };
  },
  mounted() {
    document.title = "登录 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    if (!familyAuth.getToken()) {
      familyAuth.logout();
    }
    if (familyAuth.isLoggedIn()) {
      this.$router.replace("/family");
    }
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    forgotHint() {
      this.$message.info("请联系客服 400-888-0000 重置密码");
    },
    async handleLogin() {
      if (this.loading) return;
      if (!/^1\d{10}$/.test(this.form.mobile)) {
        this.$message.warning("请输入正确的手机号");
        return;
      }
      if (!this.form.password) {
        this.$message.warning("请输入密码");
        return;
      }
      this.loading = true;
      try {
        await familyAuth.login(this.form.mobile, this.form.password);
        const redirect = this.$route.query.redirect || "/family";
        this.$router.replace(redirect);
      } catch (e) {
        this.$message.error((e && e.message) || "登录失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.family-auth {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}
.auth-field {
  margin-top: 0.375rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background-color: #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.auth-field::placeholder {
  color: #94a3b8;
}
.auth-field:focus {
  outline: none;
  border-color: #0082d4;
  box-shadow: 0 0 0 4px rgba(0, 130, 212, 0.1);
  background-color: #fff;
}
</style>
